import { useEffect, useRef, useState } from "react";
import { FaClock, FaEye, FaUserPlus, FaUsers, FaFileImage } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import figmaApi from "../api/figmaApi";
import InvitationModal from "../components/modals/InvitationModal";
import DesignUserManagementModal from "../components/modals/DesignUserManagementModal";
import { useAuth } from "../hooks/useAuth";
import { DesignSummary } from "../types";
import { DesignInvitationModalData } from "../types/modal";
import { checkIsAdmin } from "../utils/auth-utils";
import { formatDateString } from "../utils/common-utils";

/**
 * Displays all the design files accessible to this user.
 * @returns
 */
function Dashboard() {
  const { userData } = useAuth();
  const hasFetchedData = useRef(false);
  const [data, setData] = useState<Array<DesignSummary>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [invitationModalData, setInvitationModalData] =
    useState<DesignInvitationModalData | null>(null);
  const [userManagementModalData, setUserManagementModalData] =
    useState<DesignInvitationModalData | null>(null);

  // fetch all accessible designs for this user
  useEffect(() => {
    if (!userData || hasFetchedData.current) return;
    hasFetchedData.current = true;
    setIsLoading(true);

    // fetch all designs available to this user
    const getDesignsOfUser = async () => {
      try {
        const response = await figmaApi.getFigmaFrames();

        if (response.data.statusCode !== 200) {
          throw response.data.message;
        }

        setData(response.data.data);
      } catch (error) {
        console.error(error);
        toast("Did not get any data to display.", { type: "error" });
      } finally {
        setIsLoading(false);
      }
    };

    getDesignsOfUser();
  }, [userData]);

  // open the user invitation modal
  const handleInviteClick = (item: DesignSummary) => {
    setInvitationModalData({
      design_id: item.design_id,
      design_name: item.design_name,
    });
  };

  // open the user management modal
  const handleUserManagementClick = (item: DesignSummary) => {
    setUserManagementModalData({
      design_id: item.design_id,
      design_name: item.design_name,
    });
  };

  // Calculate statistics
  const totalPages = data.reduce((sum, item) => sum + item.frame_count, 0);
  const pendingUpdates = data.reduce((sum, item) => sum + item.pending_updates_count, 0);

  return (
    <div className="App">

      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner spinner-lg"></div>
        </div>
      )}

      <main className="dashboard">
        <div className="container">
          {/* Hero Section */}
          <div className="dashboard-header">
            {/* <h1 className="dashboard-title">Figma Press Dashboard</h1> */}
            {/* <p className="dashboard-subtitle">
              Manage and collaborate on your design projects with modern efficiency
            </p> */}
          </div>

          {/* Statistics Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-icon blue">
                  <FaFileImage />
                </div>
                <div className="stat-info">
                  <h3>{data.length}</h3>
                  <p>Total Designs</p>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-icon green">
                  <FaEye />
                </div>
                <div className="stat-info">
                  <h3>{totalPages}</h3>
                  <p>Total Pages</p>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-icon orange">
                  <FaClock />
                </div>
                <div className="stat-info">
                  <h3>{pendingUpdates}</h3>
                  <p>Pending Updates</p>
                </div>
              </div>
            </div>
          </div>

          {/* Designs Section */}
          {data.length > 0 ? (
            <div className="designs-section">
              <h2 className="text-2xl font-semibold mb-6 text-primary">Project Designs</h2>
              <div className="designs-grid">
                {data.map((item) => (
                  <div key={item.design_id} className="design-card">
                    {/* Notification dot for pending updates */}
                    {item.pending_updates_count > 0 && (
                      <div className="notification-dot warning" title={`${item.pending_updates_count} update${item.pending_updates_count > 1 ? 's' : ''} pending`}>
                        {item.pending_updates_count > 99 ? '99+' : item.pending_updates_count}
                      </div>
                    )}
                    <div className="design-card-wrapper">
                      <div className="design-card-header">
                        <div className="design-icon">
                          <FaFileImage />
                        </div>
                        <h3 className="design-card-title text-truncate" title={item.design_name}>
                          {item.design_name}
                        </h3>
                      </div>

                      <div className="design-card-body">
                        <div className="design-meta-grid">
                          <div className="meta-item">
                            <span className="meta-label">Pages</span>
                            <span className="meta-value">{item.frame_count}</span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Last Updated</span>
                            <span className="meta-value">{formatDateString(item.latest_updated_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="design-card-footer">
                        <Link
                          className="btn btn-primary btn-sm"
                          to={`/${item.design_id}`}
                          title={`Last update: ${formatDateString(item.latest_updated_at)}`}
                        >
                          <FaEye />
                          View Design
                        </Link>

                        {/* Admin only buttons */}
                        {userData && checkIsAdmin(userData?.role) && (
                          <div className="admin-actions">
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleInviteClick(item)}
                              title="Invite users to edit"
                            >
                              <FaUserPlus />
                              Invite
                            </button>

                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleUserManagementClick(item)}
                              title="Manage user access"
                            >
                              <FaUsers />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            !isLoading && (
              <div className="card text-center" style={{ padding: '3rem' }}>
                <div className="card-body">
                  <div style={{ fontSize: '4rem', color: 'var(--text-tertiary)', marginBottom: '1.5rem' }}>
                    <FaFileImage />
                  </div>
                  <h3 className="card-title">No Designs Found</h3>
                  <p className="card-description">
                    You don't have access to any designs yet. Contact your administrator for access.
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </main>

      {invitationModalData && (
        <InvitationModal
          {...invitationModalData}
          onCancel={() => setInvitationModalData(null)}
        />
      )}
      {userManagementModalData && (
        <DesignUserManagementModal
          {...userManagementModalData}
          onCancel={() => setUserManagementModalData(null)}
        />
      )}
    </div>
  );
}

export default Dashboard;
